import { useCallback, useEffect, useRef, useState } from 'react';

const RM_QUERY = '(prefers-reduced-motion: reduce)';

export function prefersReducedMotion() {
  return typeof window !== 'undefined' && !!window.matchMedia && window.matchMedia(RM_QUERY).matches;
}

export function useReducedMotion() {
  const [reduced, setReduced] = useState(prefersReducedMotion);
  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const mq = window.matchMedia(RM_QUERY);
    const onChange = () => setReduced(mq.matches);
    mq.addEventListener ? mq.addEventListener('change', onChange) : mq.addListener(onChange);
    return () => (mq.removeEventListener ? mq.removeEventListener('change', onChange) : mq.removeListener(onChange));
  }, []);
  return reduced;
}

// True once the element has entered the viewport (sticky by default so reveals don't replay).
export function useInView({ threshold = 0.2, rootMargin = '0px 0px -8% 0px', once = true } = {}) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    if (typeof IntersectionObserver === 'undefined') { setInView(true); return undefined; }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) { setInView(true); if (once) io.unobserve(el); }
        else if (!once) setInView(false);
      });
    }, { threshold, rootMargin });
    io.observe(el);
    return () => io.disconnect();
  }, [threshold, rootMargin, once]);
  return [ref, inView];
}

// Progress 0..1 of a tall "beat" element whose inner stage is sticky: 0 when its top hits the viewport top,
// 1 when its bottom reaches the viewport bottom. Calls onProgress inside rAF; never re-renders.
export function useScrollProgress(onProgress, { enabled = true } = {}) {
  const ref = useRef(null);
  const cb = useRef(onProgress);
  cb.current = onProgress;
  useEffect(() => {
    if (!enabled) return undefined;
    let ticking = false;
    const update = () => {
      ticking = false;
      const el = ref.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const vh = window.innerHeight;
      const total = r.height - vh;
      const p = total > 0 ? Math.min(1, Math.max(0, -r.top / total)) : 0;
      const visible = r.bottom > -vh && r.top < vh * 2;
      cb.current(p, visible);
    };
    const onScroll = () => { if (!ticking) { ticking = true; requestAnimationFrame(update); } };
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    update();
    return () => { window.removeEventListener('scroll', onScroll); window.removeEventListener('resize', onScroll); };
  }, [enabled]);
  return ref;
}

// Drives window scroll from `from` to `to` over `duration` ms. Any real scroll intent — wheel, touch-scroll,
// arrow keys, scrollbar drag — hands control back. Plain taps and clicks do NOT stop the film.
export function useAutoplayScroll({ duration = 45000, startDelay = 900, enabled = true, getBounds }) {
  const [state, setState] = useState('idle'); // idle | playing | paused | done
  const stateRef = useRef('idle');
  const raf = useRef(0);
  const lastAuto = useRef(-1);
  const set = useCallback((s) => { stateRef.current = s; setState(s); }, []);

  const pause = useCallback(() => {
    if (stateRef.current === 'paused' || stateRef.current === 'done') return;
    cancelAnimationFrame(raf.current);
    set('paused');
  }, [set]);

  const play = useCallback(() => {
    if (stateRef.current === 'playing') return;
    const { from, to } = getBounds();
    if (window.scrollY >= to - 2) window.scrollTo(0, from);
    const y0 = Math.max(from, window.scrollY);
    const t0 = performance.now();
    const span = Math.max(1, to - from);
    set('playing');
    const step = (now) => {
      if (stateRef.current !== 'playing') return;
      // Someone else moved the page since our last write (scrollbar drag, a programmatic scrollIntoView,
      // an anchor jump): hand over before we overwrite it.
      if (lastAuto.current >= 0 && Math.abs(window.scrollY - lastAuto.current) > 4) { pause(); return; }
      const y = Math.min(to, y0 + ((now - t0) / duration) * span);
      lastAuto.current = Math.round(y);
      window.scrollTo(0, y);
      if (y >= to) { set('done'); return; }
      raf.current = requestAnimationFrame(step);
    };
    raf.current = requestAnimationFrame(step);
  }, [duration, getBounds, set, pause]);

  useEffect(() => {
    if (!enabled) return undefined;
    // If the viewer (or an automated agent) scrolls before the film starts, it never starts.
    let touchedEarly = false;
    const initialY = window.scrollY;
    const onEarly = () => { if (stateRef.current === 'idle') touchedEarly = true; };
    const onUser = () => { onEarly(); pause(); };
    const onKey = (e) => {
      if (e.key === ' ' && stateRef.current !== 'done' && e.target === document.body) { e.preventDefault(); stateRef.current === 'playing' ? pause() : play(); return; }
      if (['ArrowDown', 'ArrowUp', 'PageDown', 'PageUp', 'Home', 'End'].includes(e.key)) pause();
    };
    const onScroll = () => {
      // Mobile browsers can emit a no-op scroll event while their address bar settles.
      // Only a real position change counts as the viewer taking control.
      if (stateRef.current === 'idle') {
        if (Math.abs(window.scrollY - initialY) > 4) { touchedEarly = true; pause(); }
        return;
      }
      if (stateRef.current === 'playing' && Math.abs(window.scrollY - lastAuto.current) > 4) pause();
    };
    const onHide = () => { if (document.hidden) pause(); };
    const onNavigationOpen = () => pause();
    window.addEventListener('wheel', onUser, { passive: true });
    window.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('bruins09:pause-film', onNavigationOpen);
    document.addEventListener('visibilitychange', onHide);
    const timer = setTimeout(() => {
      if (stateRef.current !== 'idle') return;
      const { from } = getBounds();
      if (touchedEarly || window.scrollY > from + 4) { set('paused'); return; }
      play();
    }, startDelay);
    return () => {
      clearTimeout(timer);
      cancelAnimationFrame(raf.current);
      window.removeEventListener('wheel', onUser);
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('bruins09:pause-film', onNavigationOpen);
      document.removeEventListener('visibilitychange', onHide);
    };
  }, [enabled, play, pause, set, getBounds, startDelay]);

  return { state, play, pause };
}
